import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { DisconnectReason } from 'livekit-client';
import {
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  LoaderCircle,
  Mic,
  MicOff,
  Plus,
  Power,
  Radio,
  RefreshCw,
  Square,
  UserPlus,
  Users,
  Video,
  VideoOff,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SystemPreferencesMenu } from '@/components/ui/system-preferences-menu';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import LogoGeoScalaDark from '@/assets/images/logo-principal-dark.svg';
import LogoGeoScalaLight from '@/assets/images/logo-principal-light.svg';
import TeleattendanceVideoConference from '@/views/teleattendance/components/TeleattendanceVideoConference';
import { playMeetingSound } from '@/views/teleattendance/components/video-conference/meetingSounds';
import {
  addOnboardingInterviewRoomCandidate,
  fetchOnboardingInterviewRecording,
  fetchOnboardingInterviewParticipantLimit,
  fetchOnboardingInterviewVideoConnection,
  fetchPublicOnboardingInterviewVideoConnection,
  endOnboardingInterviewMeeting,
  listMyOnboardingInterviews,
  listOnboardingInterviewQueue,
  moderateOnboardingInterviewParticipant,
  startOnboardingInterviewRecording,
  stopOnboardingInterviewRecording,
  updateOnboardingInterviewNotes,
  updateOnboardingInterviewParticipantLimit,
  type InterviewParticipantModerationAction,
  type OnboardingInterview,
  type OnboardingInterviewRecording,
  type OnboardingInterviewContext,
} from '../services/onboardingInterviewService';

type MeetingReturnReason =
  | 'left'
  | 'interrupted'
  | 'duplicate'
  | 'removed'
  | 'ended';

type MeetingJoinOptions = {
  audioEnabled: boolean;
  videoEnabled: boolean;
  audioInputDeviceId?: string;
  videoInputDeviceId?: string;
  audioOutputDeviceId?: string;
  microphoneSensitivity: number;
};

const DEFAULT_DEVICE_VALUE = 'default-device';

const copyTextToClipboard = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
};

const formatCpf = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

const formatBirthDate = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits
    .replace(/^(\d{2})(\d)/, '$1/$2')
    .replace(/^(\d{2})\/(\d{2})(\d)/, '$1/$2/$3');
};

const getMeetingConnectionErrorMessage = (
  error: unknown,
  fallbackMessage: string,
  formatStatusError: (status: number) => string,
  formatConnectionError: (message: string) => string,
) => {
  if (!error) return fallbackMessage;

  const requestError = error as {
    message?: string;
    response?: {
      status?: number;
      data?: {
        error?: string;
        message?: string;
        detail?: string;
      };
    };
  };
  const apiMessage =
    requestError?.response?.data?.error ||
    requestError?.response?.data?.message ||
    requestError?.response?.data?.detail;
  if (apiMessage) return apiMessage;

  if (requestError?.response?.status) {
    return formatStatusError(requestError.response.status);
  }

  if (requestError?.message) {
    return formatConnectionError(requestError.message);
  }

  return fallbackMessage;
};

const parseMeetingDate = (value?: string) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatMeetingDuration = (milliseconds: number) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');
  if (hours <= 0) return `${paddedMinutes}:${paddedSeconds}`;
  return `${String(hours).padStart(2, '0')}:${paddedMinutes}:${paddedSeconds}`;
};

export default function OnboardingInterviewMeetingPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { interviewId: interviewIdParam, token } = useParams();
  const interviewId = Number(interviewIdParam);
  const isPublicMeeting = Boolean(token);
  const [participantCpf, setParticipantCpf] = useState('');
  const [participantBirthDate, setParticipantBirthDate] = useState('');
  const [joinOptions, setJoinOptions] = useState<MeetingJoinOptions>({
    audioEnabled: true,
    videoEnabled: true,
    microphoneSensitivity: 100,
  });
  const [hasJoined, setHasJoined] = useState(false);
  const [meetingReturnReason, setMeetingReturnReason] =
    useState<MeetingReturnReason | null>(null);
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const [isPageHeaderHidden, setIsPageHeaderHidden] = useState(false);
  const [clockNow, setClockNow] = useState(() => new Date());
  const intentionalLeaveRef = useRef(false);
  const context: OnboardingInterviewContext = location.pathname.startsWith(
    '/my-company',
  )
    ? 'company'
    : 'establishment';
  const canLoadMeetingConnection = isPublicMeeting
    ? Boolean(token) &&
      participantCpf.trim().length > 0 &&
      participantBirthDate.length === 10
    : Number.isInteger(interviewId) && interviewId > 0;
  const calendarPath = `/${
    context === 'company' ? 'my-company' : 'my-establishment'
  }/onboarding-interviews/calendar`;

  const connectionQuery = useQuery({
    queryKey: [
      'onboarding-interview-video-connection',
      isPublicMeeting ? 'public' : context,
      isPublicMeeting ? token : interviewId,
      isPublicMeeting ? participantCpf.trim() : '',
      isPublicMeeting ? participantBirthDate : '',
      connectionAttempt,
    ],
    queryFn: () =>
      isPublicMeeting
        ? fetchPublicOnboardingInterviewVideoConnection(
            String(token),
            participantCpf.trim(),
            participantBirthDate,
          )
        : fetchOnboardingInterviewVideoConnection(context, interviewId),
    enabled: canLoadMeetingConnection,
    staleTime: 60_000,
    retry: false,
  });
  const moderationMutation = useMutation({
    mutationFn: ({
      participantIdentity,
      action,
      reason,
    }: {
      participantIdentity: string;
      action: InterviewParticipantModerationAction;
      reason?: string;
    }) =>
      moderateOnboardingInterviewParticipant(
        context,
        interviewId,
        participantIdentity,
        action,
        reason,
      ),
    onSuccess: (_, variables) => {
      const labels: Record<InterviewParticipantModerationAction, string> = {
        mute_microphone: t('onboarding_interviews.meeting.moderation_muted'),
        disable_camera: t(
          'onboarding_interviews.meeting.moderation_camera_disabled',
        ),
        allow_microphone: t(
          'onboarding_interviews.meeting.moderation_microphone_allowed',
        ),
        allow_camera: t(
          'onboarding_interviews.meeting.moderation_camera_allowed',
        ),
        disable_screen_share: 'Apresentação encerrada.',
        allow_screen_share: 'Apresentação liberada.',
        disconnect: t('onboarding_interviews.meeting.moderation_disconnected'),
        remove: t('onboarding_interviews.meeting.moderation_removed'),
      };
      toast({ title: labels[variables.action], variant: 'success' });
    },
    onError: (error: any) =>
      toast({
        title:
          error?.response?.data?.error ||
          t('onboarding_interviews.meeting.moderation_error'),
        variant: 'destructive',
      }),
  });
  const endMeetingMutation = useMutation({
    mutationFn: () => endOnboardingInterviewMeeting(context, interviewId),
    onSuccess: () => {
      intentionalLeaveRef.current = true;
      toast({
        title: t('onboarding_interviews.meeting.end_meeting_success'),
        variant: 'success',
      });
      showMeetingReturnState('ended');
    },
    onError: (error: any) =>
      toast({
        title:
          error?.response?.data?.error ||
          t('onboarding_interviews.meeting.end_meeting_error'),
        variant: 'destructive',
      }),
  });

  useEffect(() => {
    if (
      !hasJoined ||
      (!connectionQuery.data?.startedAt &&
        !connectionQuery.data?.scheduledStartAt)
    ) {
      return undefined;
    }
    const interval = window.setInterval(() => setClockNow(new Date()), 1000);
    return () => window.clearInterval(interval);
  }, [
    connectionQuery.data?.scheduledStartAt,
    connectionQuery.data?.startedAt,
    hasJoined,
  ]);

  const meetingScheduleLabel = useMemo(() => {
    const scheduledStart = parseMeetingDate(
      connectionQuery.data?.scheduledStartAt,
    );
    const startedAt = parseMeetingDate(connectionQuery.data?.startedAt);
    const durationStart = startedAt || scheduledStart;
    if (!durationStart) return t('onboarding_interviews.meeting.title');

    const dateLabel = format(scheduledStart || durationStart, 'dd/MM/yyyy');

    const elapsedMilliseconds = clockNow.getTime() - durationStart.getTime();
    return (
      <span className="font-sans text-sm font-medium normal-case tracking-normal text-muted-foreground tabular-nums">
        {t('onboarding_interviews.meeting.meeting_duration_label', {
          date: dateLabel,
          duration: formatMeetingDuration(elapsedMilliseconds),
        })}
      </span>
    );
  }, [
    clockNow,
    connectionQuery.data?.scheduledStartAt,
    connectionQuery.data?.startedAt,
    t,
  ]);

  const closeWindow = () => {
    window.close();
    window.setTimeout(() => {
      if (!window.closed) {
        navigate(isPublicMeeting ? '/session/login' : calendarPath, {
          replace: true,
        });
      }
    }, 100);
  };

  const showMeetingReturnState = (reason: MeetingReturnReason) => {
    setMeetingReturnReason(reason);
    setHasJoined(false);
  };

  const handleMeetingDisconnected = (reason?: DisconnectReason) => {
    if (intentionalLeaveRef.current) return;
    if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
      showMeetingReturnState('removed');
      return;
    }
    if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
      showMeetingReturnState('duplicate');
      return;
    }
    if (
      reason === DisconnectReason.ROOM_DELETED ||
      reason === DisconnectReason.ROOM_CLOSED
    ) {
      showMeetingReturnState('ended');
      return;
    }
    showMeetingReturnState('interrupted');
  };

  if (
    !isPublicMeeting &&
    (!Number.isInteger(interviewId) || interviewId <= 0)
  ) {
    return (
      <MeetingState
        title={t('onboarding_interviews.meeting.invalid_title')}
        description={t('onboarding_interviews.meeting.invalid_description')}
        onClose={closeWindow}
      />
    );
  }

  if (!hasJoined) {
    return (
      <MeetingPreJoin
        isPublicMeeting={isPublicMeeting}
        participantCpf={participantCpf}
        participantBirthDate={participantBirthDate}
        onParticipantCpfChange={setParticipantCpf}
        onParticipantBirthDateChange={setParticipantBirthDate}
        initialAudioEnabled={joinOptions.audioEnabled}
        initialVideoEnabled={joinOptions.videoEnabled}
        initialMicrophoneSensitivity={joinOptions.microphoneSensitivity}
        initialDeviceIds={{
          audioInputDeviceId: joinOptions.audioInputDeviceId,
          videoInputDeviceId: joinOptions.videoInputDeviceId,
          audioOutputDeviceId: joinOptions.audioOutputDeviceId,
        }}
        isPreparingConnection={
          canLoadMeetingConnection &&
          connectionQuery.isLoading &&
          !connectionQuery.data
        }
        onJoin={(options) => {
          if (
            isPublicMeeting &&
            (participantCpf.trim().length === 0 ||
              participantBirthDate.length !== 10)
          ) {
            toast({
              title: t('onboarding_interviews.meeting.identity_required'),
              variant: 'destructive',
            });
            return;
          }
          intentionalLeaveRef.current = false;
          setMeetingReturnReason(null);
          setJoinOptions(options);
          setHasJoined(true);
        }}
        onClose={closeWindow}
        notice={
          meetingReturnReason
            ? {
                title: t(
                  {
                    left: 'onboarding_interviews.meeting.left_title',
                    interrupted:
                      'onboarding_interviews.meeting.reconnect_title',
                    duplicate: 'onboarding_interviews.meeting.duplicate_title',
                    removed: 'onboarding_interviews.meeting.removed_title',
                    ended: 'onboarding_interviews.meeting.ended_title',
                  }[meetingReturnReason],
                ),
                description: t(
                  {
                    left: 'onboarding_interviews.meeting.left_description',
                    interrupted:
                      'onboarding_interviews.meeting.reconnect_description',
                    duplicate:
                      'onboarding_interviews.meeting.duplicate_description',
                    removed:
                      'onboarding_interviews.meeting.removed_description',
                    ended: 'onboarding_interviews.meeting.ended_description',
                  }[meetingReturnReason],
                ),
                actionLabel: t(
                  {
                    left: 'onboarding_interviews.meeting.return_button',
                    interrupted:
                      'onboarding_interviews.meeting.reconnect_button',
                    duplicate: 'onboarding_interviews.meeting.reconnect_button',
                    removed: 'onboarding_interviews.meeting.return_button',
                    ended: 'onboarding_interviews.meeting.return_button',
                  }[meetingReturnReason],
                ),
              }
            : undefined
        }
      />
    );
  }

  if (connectionQuery.isLoading) {
    return (
      <div className="relative flex h-dvh items-center justify-center bg-background">
        <div className="absolute right-4 top-4">
          <SystemPreferencesMenu />
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <LoaderCircle className="h-5 w-5 animate-spin" />
          {t('onboarding_interviews.meeting.connecting')}
        </div>
      </div>
    );
  }

  if (connectionQuery.isError || !connectionQuery.data) {
    return (
      <MeetingState
        title={t('onboarding_interviews.meeting.open_error_title')}
        description={getMeetingConnectionErrorMessage(
          connectionQuery.error,
          t('onboarding_interviews.meeting.open_error_description'),
          (status) =>
            t('onboarding_interviews.meeting.api_status_error', { status }),
          (message) =>
            t('onboarding_interviews.meeting.api_connection_error', {
              message,
            }),
        )}
        onClose={closeWindow}
        onRetry={() => setConnectionAttempt((attempt) => attempt + 1)}
      />
    );
  }

  return (
    <>
      <TeleattendanceVideoConference
        connection={connectionQuery.data}
        open
        onOpenChange={(open) => {
          if (!open) {
            intentionalLeaveRef.current = true;
            showMeetingReturnState('left');
          }
        }}
        onDisconnected={handleMeetingDisconnected}
        layoutVariant="doctor"
        presentation="page"
        pageHeaderHidden={isPageHeaderHidden}
        pageBrand={
          <>
            <img
              src={LogoGeoScalaLight}
              alt="Geoescala"
              className="h-8 w-auto dark:hidden"
            />
            <img
              src={LogoGeoScalaDark}
              alt="Geoescala"
              className="hidden h-8 w-auto dark:block"
            />
          </>
        }
        pageActions={
          <>
            {!isPublicMeeting ? (
              <>
                <InterviewParticipantLimitControl
                  context={context}
                  interviewId={interviewId}
                />
                <InterviewRecordingControl
                  context={context}
                  interviewId={interviewId}
                />
              </>
            ) : null}
            <SystemPreferencesMenu />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9"
              aria-label={t('onboarding_interviews.meeting.hide_header')}
              title={t('onboarding_interviews.meeting.hide_header')}
              onClick={() => setIsPageHeaderHidden(true)}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </>
        }
        internalControls={
          !isPublicMeeting ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="h-12 w-12 rounded-full bg-red-700 text-white shadow-lg hover:bg-red-800 hover:text-white"
                  aria-label={t('onboarding_interviews.meeting.end_meeting')}
                  title={t('onboarding_interviews.meeting.end_meeting')}
                  disabled={endMeetingMutation.isPending}
                >
                  {endMeetingMutation.isPending ? (
                    <LoaderCircle className="h-5 w-5 animate-spin" />
                  ) : (
                    <Power className="h-5 w-5" />
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('onboarding_interviews.meeting.end_meeting_title')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('onboarding_interviews.meeting.end_meeting_description')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>
                    {t('onboarding_interviews.meeting.cancel')}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-700 text-white hover:bg-red-800"
                    disabled={endMeetingMutation.isPending}
                    onClick={() => endMeetingMutation.mutate()}
                  >
                    {t('onboarding_interviews.meeting.end_meeting_confirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : undefined
        }
        onModerateParticipant={
          isPublicMeeting
            ? undefined
            : (participantIdentity, action, reason) =>
                moderationMutation.mutate({
                  participantIdentity,
                  action,
                  reason,
                })
        }
        moderationPending={!isPublicMeeting && moderationMutation.isPending}
        chatConfig={{
          isPublic: isPublicMeeting,
          interviewId: isPublicMeeting ? undefined : interviewId,
          context: isPublicMeeting ? undefined : context,
          accessToken: connectionQuery.data.chatAccessToken,
          accessTokenExpiresAt:
            connectionQuery.data.chatAccessTokenExpiresAt,
        }}
        initialAudioEnabled={joinOptions.audioEnabled}
        initialVideoEnabled={joinOptions.videoEnabled}
        initialMicrophoneSensitivity={joinOptions.microphoneSensitivity}
        initialDeviceIds={{
          audioInputDeviceId: joinOptions.audioInputDeviceId,
          videoInputDeviceId: joinOptions.videoInputDeviceId,
          audioOutputDeviceId: joinOptions.audioOutputDeviceId,
        }}
        videoLabels={{
          main: isPublicMeeting
            ? t('onboarding_interviews.meeting.interviewer_label')
            : meetingScheduleLabel,
          preview: t('onboarding_interviews.meeting.you_label'),
        }}
        title={t('onboarding_interviews.meeting.title')}
        description=""
      />
      {!isPublicMeeting ? (
        <InterviewNotesPad context={context} interviewId={interviewId} />
      ) : null}
      {isPageHeaderHidden ? (
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className="fixed right-3 top-3 z-[80] h-11 w-11 border border-white bg-white text-slate-950 shadow-[0_12px_36px_rgba(15,23,42,0.38)] ring-2 ring-sky-500/45 hover:bg-slate-100 hover:text-slate-950 dark:border-white dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
          aria-label={t('onboarding_interviews.meeting.show_header')}
          title={t('onboarding_interviews.meeting.show_header')}
          onClick={() => setIsPageHeaderHidden(false)}
        >
          <ChevronDown className="h-5 w-5" />
        </Button>
      ) : null}
    </>
  );
}

const InterviewNotesPad = ({
  context,
  interviewId,
}: {
  context: OnboardingInterviewContext;
  interviewId: number;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div
      className="fixed left-0 top-1/2 z-[80] flex -translate-y-1/2 items-start transition-transform duration-300 ease-out will-change-transform motion-reduce:transition-none"
      style={{
        transform: open
          ? 'translate3d(0, -50%, 0)'
          : 'translate3d(calc(-1 * min(340px, calc(100vw - 52px))), -50%, 0)',
      }}
    >
      <div
        className={`w-[min(340px,calc(100vw-52px))] rounded-r-lg border border-l-0 border-border/80 bg-background shadow-xl transition-opacity duration-200 ease-out motion-reduce:transition-none ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border/70 px-3 py-2">
          <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-sky-800 dark:text-sky-200">
            <FileText className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {t('onboarding_interviews.meeting.notes_title')}
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-md"
            aria-label={t('onboarding_interviews.meeting.cancel')}
            onClick={() => setOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="px-3 py-3">
          <InterviewNotesPanel context={context} interviewId={interviewId} compact />
        </div>
      </div>
      <Button
        type="button"
        variant="default"
        className="mt-2 h-auto rounded-l-none rounded-r-md border border-l-0 border-sky-700 bg-sky-700 px-2 py-3 text-white shadow-lg transition-colors hover:bg-sky-800 hover:text-white"
        aria-label={t('onboarding_interviews.meeting.notes_title')}
        aria-expanded={open}
        title={t('onboarding_interviews.meeting.notes_title')}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="flex flex-col items-center gap-2">
          <FileText className="h-4 w-4" />
          <span className="hidden text-xs font-semibold [writing-mode:vertical-rl] sm:inline">
            {t('onboarding_interviews.meeting.notes_title')}
          </span>
        </span>
      </Button>
    </div>
  );
};

const InterviewNotesPanel = ({
  context,
  interviewId,
  compact = false,
}: {
  context: OnboardingInterviewContext;
  interviewId: number;
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const loadedInterviewIdRef = useRef<number | null>(null);
  const latestSaveRequestRef = useRef(0);
  const notesRef = useRef('');
  const [notes, setNotes] = useState('');
  const [lastSavedNotes, setLastSavedNotes] = useState('');
  const queryKey = ['onboarding-interviews-mine', context];
  const interviewsQuery = useQuery({
    queryKey,
    queryFn: () => listMyOnboardingInterviews(context),
    enabled: Number.isInteger(interviewId) && interviewId > 0,
    staleTime: 15_000,
  });
  const interview = useMemo(
    () => interviewsQuery.data?.find((item) => item.id === interviewId),
    [interviewId, interviewsQuery.data],
  );
  const saveMutation = useMutation({
    mutationFn: ({
      nextNotes,
    }: {
      nextNotes: string;
      requestID: number;
    }) => updateOnboardingInterviewNotes(context, interviewId, nextNotes),
    onSuccess: (updated, variables) => {
      if (variables.requestID !== latestSaveRequestRef.current) return;
      const savedNotes = updated.notes || '';
      if (notesRef.current === variables.nextNotes) {
        setNotes(savedNotes);
        notesRef.current = savedNotes;
      }
      setLastSavedNotes(savedNotes);
      queryClient.setQueryData<OnboardingInterview[]>(queryKey, (current) =>
        current?.map((item) => (item.id === updated.id ? updated : item)),
      );
    },
    onError: (error: any) =>
      toast({
        title:
          error?.response?.data?.error ||
          t('onboarding_interviews.meeting.notes_save_error'),
        variant: 'destructive',
      }),
  });

  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);

  useEffect(() => {
    if (loadedInterviewIdRef.current === interviewId) return;
    if (interviewsQuery.isLoading) return;
    const initialNotes = interview?.notes || '';
    loadedInterviewIdRef.current = interviewId;
    setNotes(initialNotes);
    setLastSavedNotes(initialNotes);
  }, [interview?.notes, interviewId, interviewsQuery.isLoading]);

  const isDirty = notes !== lastSavedNotes;
  const characterCount = [...notes].length;
  const characterLimit = 30000;
  const isOverLimit = characterCount > characterLimit;
  const autosaveStatus = isOverLimit
    ? ''
    : saveMutation.isPending
      ? t('onboarding_interviews.meeting.notes_saving')
      : isDirty
        ? t('onboarding_interviews.meeting.notes_pending')
        : t('onboarding_interviews.meeting.notes_saved_state');

  useEffect(() => {
    if (loadedInterviewIdRef.current !== interviewId) return undefined;
    if (!isDirty || isOverLimit) return undefined;
    const timeout = window.setTimeout(() => {
      const requestID = latestSaveRequestRef.current + 1;
      latestSaveRequestRef.current = requestID;
      saveMutation.mutate({ nextNotes: notes, requestID });
    }, 900);
    return () => window.clearTimeout(timeout);
  }, [interviewId, isDirty, isOverLimit, notes, saveMutation]);

  return (
    <section
      className={`flex flex-col ${compact ? 'gap-2' : 'h-full min-h-[220px] gap-3'}`}
    >
      {!compact ? (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-sky-700" />
          <h2 className="text-sm font-semibold text-card-foreground">
            {t('onboarding_interviews.meeting.notes_title')}
          </h2>
        </div>
      ) : null}
      <Textarea
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        placeholder={t('onboarding_interviews.meeting.notes_placeholder')}
        className={`resize-none bg-background leading-6 ${
          compact ? 'h-[180px] min-h-[180px]' : 'min-h-[180px] flex-1'
        }`}
        maxLength={characterLimit + 1}
      />
      <div
        className={
          compact
            ? 'flex items-center justify-between gap-2'
            : 'flex flex-col gap-2'
        }
      >
        <div
          className={`shrink-0 text-xs ${
            isOverLimit ? 'text-destructive' : 'text-muted-foreground'
          }`}
        >
          {t('onboarding_interviews.meeting.notes_counter', {
            count: characterCount,
            limit: characterLimit,
          })}
        </div>
        {autosaveStatus ? (
          <div className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground">
            {saveMutation.isPending ? (
              <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            {autosaveStatus}
          </div>
        ) : null}
      </div>
    </section>
  );
};

const recordingIsActive = (recording?: OnboardingInterviewRecording | null) =>
  recording?.status === 'starting' ||
  recording?.status === 'active' ||
  recording?.status === 'ending';

const recordingCanStop = (recording?: OnboardingInterviewRecording | null) =>
  recording?.status === 'starting' || recording?.status === 'active';

const recordingIsDownloadable = (
  recording?: OnboardingInterviewRecording | null,
) => recording?.status === 'complete';

const InterviewParticipantLimitControl = ({
  context,
  interviewId,
}: {
  context: OnboardingInterviewContext;
  interviewId: number;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState('');
  const queryKey = ['onboarding-interview-participant-limit', context, interviewId];
  const participantLimitQuery = useQuery({
    queryKey,
    queryFn: () => fetchOnboardingInterviewParticipantLimit(context, interviewId),
    enabled: Number.isInteger(interviewId) && interviewId > 0,
    refetchInterval: 5000,
    retry: false,
  });
  const candidateQueueQuery = useQuery({
    queryKey: ['onboarding-interview-queue', context],
    queryFn: () => listOnboardingInterviewQueue(context),
    enabled: candidateDialogOpen,
    staleTime: 15_000,
  });
  const increaseMutation = useMutation({
    mutationFn: () =>
      updateOnboardingInterviewParticipantLimit(context, interviewId, {
        delta: 1,
      }),
    onSuccess: (limit) => {
      queryClient.setQueryData(queryKey, limit);
      setCandidateDialogOpen(false);
      setSelectedProfessionalId('');
      toast({
        title: t('onboarding_interviews.meeting.participant_limit_updated'),
        description: t(
          'onboarding_interviews.meeting.participant_limit_updated_description',
          { limit: limit.effective_limit },
        ),
        variant: 'success',
      });
      return limit;
    },
    onError: () =>
      toast({
        title: t('onboarding_interviews.meeting.participant_limit_error'),
        variant: 'destructive',
      }),
  });
  const candidateMutation = useMutation({
    mutationFn: (professionalId: number) =>
      addOnboardingInterviewRoomCandidate(context, interviewId, professionalId),
    onSuccess: async (candidate) => {
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({
        queryKey: ['onboarding-interview-queue', context],
      });
      setCandidateDialogOpen(false);
      setSelectedProfessionalId('');
      const meetingLink =
        candidate.public_link?.meeting_link ||
        candidate.public_link?.landing_url ||
        candidate.public_link?.public_url ||
        (candidate.public_link?.public_path
          ? `${window.location.origin}${candidate.public_link.public_path}`
          : '');
      if (meetingLink) {
        await copyTextToClipboard(meetingLink);
      }
      toast({
        title: t('onboarding_interviews.meeting.candidate_release_success'),
        description: candidate.public_link?.email_sent
          ? t('onboarding_interviews.meeting.candidate_release_email_sent')
          : t('onboarding_interviews.meeting.candidate_release_link_copied'),
        variant: 'success',
      });
    },
    onError: (error: any) =>
      toast({
        title:
          error?.response?.data?.error ||
          t('onboarding_interviews.meeting.candidate_release_error'),
        variant: 'destructive',
      }),
  });
  const limit = participantLimitQuery.data;
  const effectiveLimit = limit?.effective_limit || limit?.automatic_limit || 1;
  const activeCount = limit?.active_count ?? 0;
  const maxLimit = limit?.max_limit || 20;
  const canIncrease = effectiveLimit < maxLimit;
  const availableCandidates = candidateQueueQuery.data || [];
  const selectedCandidateID = Number(selectedProfessionalId);
  const pending =
    participantLimitQuery.isLoading ||
    increaseMutation.isPending ||
    candidateMutation.isPending;

  return (
    <div className="flex items-center gap-2 rounded-md border border-border/70 bg-background/85 px-2 py-1.5 shadow-sm backdrop-blur">
      <span
        className="flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-muted-foreground"
        title={t('onboarding_interviews.meeting.participant_limit_status')}
      >
        <Users className="h-4 w-4" />
        {activeCount}/{effectiveLimit}
      </span>
      <Dialog open={candidateDialogOpen} onOpenChange={setCandidateDialogOpen}>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 rounded-md"
            disabled={pending || !canIncrease}
            title={t('onboarding_interviews.meeting.participant_limit_increase')}
          >
            {pending ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="mr-2 h-4 w-4" />
            )}
            {t('onboarding_interviews.meeting.participant_limit_increase')}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>
              {t('onboarding_interviews.meeting.release_participant_title')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'onboarding_interviews.meeting.release_participant_description',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-2">
              <label className="text-sm font-medium text-foreground">
                {t('onboarding_interviews.meeting.available_candidate')}
              </label>
              <Select
                value={selectedProfessionalId}
                onValueChange={setSelectedProfessionalId}
                disabled={
                  candidateQueueQuery.isLoading ||
                  candidateMutation.isPending ||
                  availableCandidates.length === 0
                }
              >
                <SelectTrigger className="rounded-md">
                  <SelectValue
                    placeholder={
                      candidateQueueQuery.isLoading
                        ? t('onboarding_interviews.meeting.loading_candidates')
                        : t('onboarding_interviews.meeting.select_candidate')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableCandidates.map((candidate) => (
                    <SelectItem
                      key={candidate.professional_id}
                      value={String(candidate.professional_id)}
                    >
                      {candidate.professional_name || 'Candidato'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!candidateQueueQuery.isLoading &&
              availableCandidates.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t('onboarding_interviews.meeting.no_candidates_available')}
                </p>
              ) : null}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-md"
              disabled={increaseMutation.isPending || !canIncrease}
              onClick={() => increaseMutation.mutate()}
            >
              {increaseMutation.isPending ? (
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {t('onboarding_interviews.meeting.only_increase_limit')}
            </Button>
            <Button
              type="button"
              className="rounded-md"
              disabled={!selectedCandidateID || candidateMutation.isPending}
              onClick={() => candidateMutation.mutate(selectedCandidateID)}
            >
              {candidateMutation.isPending ? (
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="mr-2 h-4 w-4" />
              )}
              {t('onboarding_interviews.meeting.release_candidate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InterviewRecordingControl = ({
  context,
  interviewId,
}: {
  context: OnboardingInterviewContext;
  interviewId: number;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const recordingQuery = useQuery({
    queryKey: ['onboarding-interview-recording', context, interviewId],
    queryFn: () => fetchOnboardingInterviewRecording(context, interviewId),
    enabled: Number.isInteger(interviewId) && interviewId > 0,
    refetchInterval: (query) =>
      recordingIsActive(query.state.data) ? 4000 : false,
    retry: false,
  });
  const startMutation = useMutation({
    mutationFn: () => startOnboardingInterviewRecording(context, interviewId),
    onSuccess: (recording) => {
      recordingQuery.refetch();
      toast({
        title: t('onboarding_interviews.meeting.recording_started'),
        variant: 'success',
      });
      return recording;
    },
    onError: () =>
      toast({
        title: t('onboarding_interviews.meeting.recording_error'),
        variant: 'destructive',
      }),
  });
  const stopMutation = useMutation({
    mutationFn: () => stopOnboardingInterviewRecording(context, interviewId),
    onSuccess: () => {
      recordingQuery.refetch();
      toast({
        title: t('onboarding_interviews.meeting.recording_stopped'),
        variant: 'success',
      });
    },
    onError: () =>
      toast({
        title: t('onboarding_interviews.meeting.recording_error'),
        variant: 'destructive',
      }),
  });
  const recording = recordingQuery.data;
  const pending = startMutation.isPending || stopMutation.isPending;

  const downloadRecording = async () => {
    try {
      const response = await fetchOnboardingInterviewRecording(
        context,
        interviewId,
        true,
      );
      if (!response?.download_url) {
        toast({
          title: t('onboarding_interviews.meeting.recording_not_ready'),
          variant: 'destructive',
        });
        return;
      }
      window.open(response.download_url, '_blank', 'noopener,noreferrer');
    } catch {
      toast({
        title: t('onboarding_interviews.meeting.recording_error'),
        variant: 'destructive',
      });
    }
  };

  if (recordingCanStop(recording)) {
    return (
      <Button
        type="button"
        variant="destructive"
        size="sm"
        className="h-9 rounded-md"
        disabled={pending}
        onClick={() => stopMutation.mutate()}
      >
        {pending ? (
          <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Square className="mr-2 h-4 w-4" />
        )}
        {t('onboarding_interviews.meeting.stop_recording')}
      </Button>
    );
  }

  if (recording?.status === 'ending') {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 rounded-md"
        disabled
      >
        <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
        {t('onboarding_interviews.meeting.recording_stopped')}
      </Button>
    );
  }

  if (recordingIsDownloadable(recording)) {
    return (
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 rounded-md"
          onClick={() => void downloadRecording()}
        >
          <Download className="mr-2 h-4 w-4" />
          {t('onboarding_interviews.meeting.download_recording')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 rounded-md"
          disabled={pending}
          onClick={() => startMutation.mutate()}
        >
          {pending ? (
            <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          {t('onboarding_interviews.meeting.start_recording_again')}
        </Button>
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-9 rounded-md"
      disabled={pending}
      onClick={() => startMutation.mutate()}
    >
      {pending ? (
        <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Radio className="mr-2 h-4 w-4 text-red-600" />
      )}
      {t('onboarding_interviews.meeting.start_recording')}
    </Button>
  );
};

const MeetingPreJoin = ({
  isPublicMeeting,
  participantCpf,
  participantBirthDate,
  onParticipantCpfChange,
  onParticipantBirthDateChange,
  initialAudioEnabled,
  initialVideoEnabled,
  initialMicrophoneSensitivity,
  initialDeviceIds,
  isPreparingConnection,
  onJoin,
  onClose,
  notice,
}: {
  isPublicMeeting: boolean;
  participantCpf: string;
  participantBirthDate: string;
  onParticipantCpfChange: (cpf: string) => void;
  onParticipantBirthDateChange: (birthDate: string) => void;
  initialAudioEnabled: boolean;
  initialVideoEnabled: boolean;
  initialMicrophoneSensitivity: number;
  initialDeviceIds?: {
    audioInputDeviceId?: string;
    videoInputDeviceId?: string;
    audioOutputDeviceId?: string;
  };
  isPreparingConnection?: boolean;
  onJoin: (options: MeetingJoinOptions) => void;
  onClose: () => void;
  notice?: {
    title: string;
    description: string;
    actionLabel: string;
  };
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const microphoneTestCleanupRef = useRef<(() => void) | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(initialAudioEnabled);
  const [videoEnabled, setVideoEnabled] = useState(initialVideoEnabled);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioInputDeviceId, setAudioInputDeviceId] = useState(
    initialDeviceIds?.audioInputDeviceId || '',
  );
  const [videoInputDeviceId, setVideoInputDeviceId] = useState(
    initialDeviceIds?.videoInputDeviceId || '',
  );
  const [audioOutputDeviceId, setAudioOutputDeviceId] = useState(
    initialDeviceIds?.audioOutputDeviceId || '',
  );
  const [mediaError, setMediaError] = useState('');
  const [microphoneTestActive, setMicrophoneTestActive] = useState(false);
  const [microphoneTestLevel, setMicrophoneTestLevel] = useState(0);
  const [microphoneSensitivity, setMicrophoneSensitivity] = useState(
    initialMicrophoneSensitivity,
  );
  const microphoneSensitivityRef = useRef(initialMicrophoneSensitivity);
  microphoneSensitivityRef.current = microphoneSensitivity;
  const audioInputDevices = useMemo(
    () => devices.filter((device) => device.kind === 'audioinput'),
    [devices],
  );
  const videoInputDevices = useMemo(
    () => devices.filter((device) => device.kind === 'videoinput'),
    [devices],
  );
  const audioOutputDevices = useMemo(
    () => devices.filter((device) => device.kind === 'audiooutput'),
    [devices],
  );
  const canSelectAudioOutput =
    typeof HTMLMediaElement !== 'undefined' &&
    'setSinkId' in HTMLMediaElement.prototype;

  const refreshDevices = async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const nextDevices = await navigator.mediaDevices.enumerateDevices();
    setDevices(nextDevices);
  };

  const toggleAudio = () => {
    setAudioEnabled((enabled) => {
      const nextEnabled = !enabled;
      playMeetingSound(nextEnabled ? 'enable' : 'disable');
      return nextEnabled;
    });
  };

  const toggleVideo = () => {
    setVideoEnabled((enabled) => {
      const nextEnabled = !enabled;
      playMeetingSound(nextEnabled ? 'enable' : 'disable');
      return nextEnabled;
    });
  };

  const playAudioTest = async () => {
    try {
      setMediaError('');
      const AudioContextConstructor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioContextConstructor) {
        throw new Error('audio context unavailable');
      }
      const audioContext = new AudioContextConstructor();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const destination = audioContext.createMediaStreamDestination();
      const audio = new Audio();
      audio.srcObject = destination.stream;

      const setSinkId = (audio as HTMLAudioElement & {
        setSinkId?: (sinkId: string) => Promise<void>;
      }).setSinkId;
      if (audioOutputDeviceId && setSinkId) {
        await setSinkId.call(audio, audioOutputDeviceId);
      }

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(720, audioContext.currentTime);
      gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(
        0.16,
        audioContext.currentTime + 0.04,
      );
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        audioContext.currentTime + 0.55,
      );
      oscillator.connect(gain);
      gain.connect(destination);
      oscillator.start();
      await audio.play();
      window.setTimeout(() => {
        oscillator.stop();
        audio.pause();
        destination.stream.getTracks().forEach((track) => track.stop());
        void audioContext.close();
      }, 650);
    } catch {
      setMediaError('onboarding_interviews.meeting.audio_test_error');
    }
  };

  const stopMicrophoneTest = () => {
    microphoneTestCleanupRef.current?.();
    microphoneTestCleanupRef.current = null;
    setMicrophoneTestActive(false);
    setMicrophoneTestLevel(0);
  };

  const testMicrophone = async () => {
    stopMicrophoneTest();
    try {
      setMediaError('');
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('media devices unavailable');
      }
      const AudioContextConstructor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioContextConstructor) {
        throw new Error('audio context unavailable');
      }
      const testStream = await navigator.mediaDevices.getUserMedia({
        audio: audioInputDeviceId
          ? {
              autoGainControl: false,
              deviceId: { exact: audioInputDeviceId },
            }
          : { autoGainControl: false },
        video: false,
      });
      const audioContext = new AudioContextConstructor();
      const source = audioContext.createMediaStreamSource(testStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);
      let animationFrame = 0;
      const updateLevel = () => {
        analyser.getByteTimeDomainData(samples);
        const sum = samples.reduce((total, sample) => {
          const normalized = (sample - 128) / 128;
          return total + normalized * normalized;
        }, 0);
        const rms = Math.sqrt(sum / samples.length);
        setMicrophoneTestLevel(
          Math.min(
            100,
            Math.round(
              rms * 260 * (microphoneSensitivityRef.current / 100),
            ),
          ),
        );
        animationFrame = window.requestAnimationFrame(updateLevel);
      };
      const timeout = window.setTimeout(stopMicrophoneTest, 5000);
      microphoneTestCleanupRef.current = () => {
        window.cancelAnimationFrame(animationFrame);
        window.clearTimeout(timeout);
        source.disconnect();
        testStream.getTracks().forEach((track) => track.stop());
        void audioContext.close();
      };
      setMicrophoneTestActive(true);
      updateLevel();
    } catch {
      setMediaError('onboarding_interviews.meeting.microphone_test_error');
      stopMicrophoneTest();
    }
  };

  useEffect(() => {
    void refreshDevices().catch(() => undefined);
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.addEventListener) return undefined;
    const handleDeviceChange = () => {
      void refreshDevices().catch(() => undefined);
    };
    mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  useEffect(
    () => () => {
      stopMicrophoneTest();
    },
    [],
  );

  useEffect(() => {
    setAudioInputDeviceId((currentDeviceId) =>
      currentDeviceId &&
      !audioInputDevices.some((device) => device.deviceId === currentDeviceId)
        ? ''
        : currentDeviceId,
    );
    setVideoInputDeviceId((currentDeviceId) =>
      currentDeviceId &&
      !videoInputDevices.some((device) => device.deviceId === currentDeviceId)
        ? ''
        : currentDeviceId,
    );
    setAudioOutputDeviceId((currentDeviceId) =>
      currentDeviceId &&
      !audioOutputDevices.some((device) => device.deviceId === currentDeviceId)
        ? ''
        : currentDeviceId,
    );
  }, [audioInputDevices, audioOutputDevices, videoInputDevices]);

  useEffect(() => {
    let cancelled = false;
    const stopPreview = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    };

    const startPreview = async () => {
      stopPreview();
      setMediaError('');
      if (!audioEnabled && !videoEnabled) return;
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('media devices unavailable');
        }
        const stream = new MediaStream();
        let mediaAccessFailed = false;
        if (audioEnabled) {
          try {
            const audioStream = await navigator.mediaDevices.getUserMedia({
              audio: audioInputDeviceId
                ? { deviceId: { exact: audioInputDeviceId } }
                : true,
              video: false,
            });
            audioStream.getAudioTracks().forEach((track) => {
              stream.addTrack(track);
            });
          } catch {
            mediaAccessFailed = true;
            setAudioEnabled(false);
          }
        }
        if (videoEnabled) {
          try {
            const videoStream = await navigator.mediaDevices.getUserMedia({
              audio: false,
              video: videoInputDeviceId
                ? { deviceId: { exact: videoInputDeviceId } }
                : true,
            });
            videoStream.getVideoTracks().forEach((track) => {
              stream.addTrack(track);
            });
          } catch {
            mediaAccessFailed = true;
            setVideoEnabled(false);
          }
        }
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        if (mediaAccessFailed) {
          setMediaError('onboarding_interviews.meeting.media_error');
        }
        void refreshDevices().catch(() => undefined);
        if (stream.getTracks().length === 0) {
          return;
        }
        streamRef.current = stream;
        if (videoRef.current && stream.getVideoTracks().length > 0) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play().catch(() => undefined);
        }
      } catch {
        setAudioEnabled(false);
        setVideoEnabled(false);
        setMediaError('onboarding_interviews.meeting.media_error');
      }
    };

    void startPreview();
    return () => {
      cancelled = true;
      stopPreview();
    };
  }, [audioEnabled, audioInputDeviceId, videoEnabled, videoInputDeviceId]);

  const selectClassName =
    'h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none focus:border-ring disabled:cursor-not-allowed disabled:opacity-60';
  const deviceOptionLabel = (
    device: MediaDeviceInfo,
    fallbackKey: string,
    index: number,
  ) => device.label || `${t(fallbackKey)} ${index + 1}`;
  const resolveDeviceSelection = (value: string) =>
    value === DEFAULT_DEVICE_VALUE ? '' : value;

  return (
    <div className="flex h-dvh min-h-0 flex-col bg-background text-card-foreground">
      <header className="flex min-h-16 items-center justify-between border-b border-border bg-card px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <img
            src={LogoGeoScalaLight}
            alt="Geoescala"
            className="h-8 w-auto dark:hidden"
          />
          <img
            src={LogoGeoScalaDark}
            alt="Geoescala"
            className="hidden h-8 w-auto dark:block"
          />
          <div className="h-7 w-px bg-border" />
          <div>
            <h1 className="text-sm font-semibold sm:text-base">
              {t('onboarding_interviews.meeting.title')}
            </h1>
            <p className="text-xs text-muted-foreground">
              {t('onboarding_interviews.meeting.prepare_entry')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SystemPreferencesMenu />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full text-card-foreground hover:bg-accent hover:text-accent-foreground"
            onClick={onClose}
            aria-label={t('common.close')}
            title={t('common.close')}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <main className="grid min-h-0 flex-1 grid-cols-1 gap-5 p-4 sm:p-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <section className="flex min-h-0 items-center justify-center">
          <div className="relative aspect-video w-full max-w-5xl overflow-hidden rounded-lg border border-border bg-card shadow-[0_18px_42px_rgba(15,23,42,0.14)] dark:shadow-[0_22px_60px_rgba(0,0,0,0.32)]">
            {videoEnabled ? (
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="h-full w-full scale-x-[-1] object-cover"
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-border bg-background/85 text-foreground shadow-sm">
                  <VideoOff className="h-10 w-10 text-muted-foreground" />
                </div>
                <span className="text-sm font-medium">
                  {t('onboarding_interviews.meeting.camera_off')}
                </span>
              </div>
            )}
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-3">
              <div className="relative">
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className={
                    audioEnabled
                      ? 'h-12 w-12 rounded-full border-border bg-card text-card-foreground shadow-sm hover:bg-accent hover:text-accent-foreground'
                      : 'h-12 w-12 rounded-full border-red-600 bg-red-600 text-white hover:bg-red-700 hover:text-white'
                  }
                  onClick={toggleAudio}
                  title={
                    audioEnabled
                      ? t('onboarding_interviews.meeting.disable_microphone')
                      : t('onboarding_interviews.meeting.enable_microphone')
                  }
                >
                  {audioEnabled ? <Mic size={21} /> : <MicOff size={21} />}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-card-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
                      aria-label={t(
                        'onboarding_interviews.meeting.audio_settings',
                      )}
                      title={t(
                        'onboarding_interviews.meeting.audio_settings',
                      )}
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="top"
                    align="center"
                    className="w-80 p-3"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="grid gap-3">
                      <p className="text-sm font-semibold text-popover-foreground">
                        {t('onboarding_interviews.meeting.audio_settings')}
                      </p>
                      <label className="grid gap-1.5">
                        <span className="text-xs font-medium text-muted-foreground">
                          {t('onboarding_interviews.meeting.microphone_input')}
                        </span>
                        <select
                          value={audioInputDeviceId || DEFAULT_DEVICE_VALUE}
                          onChange={(event) =>
                            setAudioInputDeviceId(
                              resolveDeviceSelection(event.target.value),
                            )
                          }
                          className={selectClassName}
                        >
                          <option value={DEFAULT_DEVICE_VALUE}>
                            {t('onboarding_interviews.meeting.default_device')}
                          </option>
                          {audioInputDevices.map((device, index) => (
                            <option
                              key={device.deviceId}
                              value={device.deviceId}
                            >
                              {deviceOptionLabel(
                                device,
                                'onboarding_interviews.meeting.microphone_input',
                                index,
                              )}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="grid gap-2">
                        <span className="flex items-center justify-between gap-3 text-xs font-medium text-muted-foreground">
                          <span>
                            {t(
                              'onboarding_interviews.meeting.microphone_sensitivity',
                            )}
                          </span>
                          <span className="tabular-nums text-foreground">
                            {microphoneSensitivity}%
                          </span>
                        </span>
                        <Slider
                          min={25}
                          max={400}
                          step={5}
                          value={[microphoneSensitivity]}
                          onValueChange={([value]) =>
                            setMicrophoneSensitivity(value)
                          }
                          aria-label={t(
                            'onboarding_interviews.meeting.microphone_sensitivity',
                          )}
                        />
                      </label>
                      <label className="grid gap-1.5">
                        <span className="text-xs font-medium text-muted-foreground">
                          {t('onboarding_interviews.meeting.speaker_output')}
                        </span>
                        <select
                          value={audioOutputDeviceId || DEFAULT_DEVICE_VALUE}
                          onChange={(event) =>
                            setAudioOutputDeviceId(
                              resolveDeviceSelection(event.target.value),
                            )
                          }
                          className={selectClassName}
                          disabled={
                            !canSelectAudioOutput ||
                            audioOutputDevices.length === 0
                          }
                        >
                          <option value={DEFAULT_DEVICE_VALUE}>
                            {t('onboarding_interviews.meeting.default_device')}
                          </option>
                          {audioOutputDevices.map((device, index) => (
                            <option
                              key={device.deviceId}
                              value={device.deviceId}
                            >
                              {deviceOptionLabel(
                                device,
                                'onboarding_interviews.meeting.speaker_output',
                                index,
                              )}
                            </option>
                          ))}
                        </select>
                      </label>
                      {!canSelectAudioOutput ? (
                        <p className="text-xs text-muted-foreground">
                          {t(
                            'onboarding_interviews.meeting.audio_output_not_supported',
                          )}
                        </p>
                      ) : null}
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 rounded-md"
                        onClick={() => void playAudioTest()}
                      >
                        {t('onboarding_interviews.meeting.test_audio')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 rounded-md"
                        onClick={() => void testMicrophone()}
                      >
                        {t('onboarding_interviews.meeting.test_microphone')}
                      </Button>
                      {microphoneTestActive ? (
                        <div className="grid gap-1.5">
                          <div className="h-2 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-emerald-500 transition-[width] duration-75"
                              style={{ width: `${microphoneTestLevel}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {t(
                              'onboarding_interviews.meeting.microphone_test_hint',
                            )}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="relative">
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className={
                    videoEnabled
                      ? 'h-12 w-12 rounded-full border-border bg-card text-card-foreground shadow-sm hover:bg-accent hover:text-accent-foreground'
                      : 'h-12 w-12 rounded-full border-red-600 bg-red-600 text-white hover:bg-red-700 hover:text-white'
                  }
                  onClick={toggleVideo}
                  title={
                    videoEnabled
                      ? t('onboarding_interviews.meeting.disable_camera')
                      : t('onboarding_interviews.meeting.enable_camera')
                  }
                >
                  {videoEnabled ? <Video size={21} /> : <VideoOff size={21} />}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-card-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
                      aria-label={t(
                        'onboarding_interviews.meeting.video_settings',
                      )}
                      title={t(
                        'onboarding_interviews.meeting.video_settings',
                      )}
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="top"
                    align="center"
                    className="w-80 p-3"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="grid gap-3">
                      <p className="text-sm font-semibold text-popover-foreground">
                        {t('onboarding_interviews.meeting.video_settings')}
                      </p>
                      <label className="grid gap-1.5">
                        <span className="text-xs font-medium text-muted-foreground">
                          {t('onboarding_interviews.meeting.camera_input')}
                        </span>
                        <select
                          value={videoInputDeviceId || DEFAULT_DEVICE_VALUE}
                          onChange={(event) =>
                            setVideoInputDeviceId(
                              resolveDeviceSelection(event.target.value),
                            )
                          }
                          className={selectClassName}
                        >
                          <option value={DEFAULT_DEVICE_VALUE}>
                            {t('onboarding_interviews.meeting.default_device')}
                          </option>
                          {videoInputDevices.map((device, index) => (
                            <option
                              key={device.deviceId}
                              value={device.deviceId}
                            >
                              {deviceOptionLabel(
                                device,
                                'onboarding_interviews.meeting.camera_input',
                                index,
                              )}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </section>

        <aside className="flex min-h-0 flex-col justify-center gap-4 rounded-lg border border-border bg-card p-5 shadow-[0_18px_42px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_48px_rgba(0,0,0,0.22)]">
          <div>
            <h2 className="text-lg font-semibold">
              {t(
                isPublicMeeting
                  ? 'onboarding_interviews.meeting.public_join_title'
                  : 'onboarding_interviews.meeting.join_title',
              )}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t(
                isPublicMeeting
                  ? 'onboarding_interviews.meeting.public_join_description'
                  : 'onboarding_interviews.meeting.join_description',
              )}
            </p>
          </div>

          {notice ? (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-100">
              <p className="font-semibold">{notice.title}</p>
              <p className="mt-1 text-xs">{notice.description}</p>
            </div>
          ) : null}

          {isPublicMeeting ? (
            <div className="grid gap-3">
              <label className="grid gap-2">
                <span className="text-sm font-medium text-card-foreground">
                  {t('onboarding_interviews.meeting.candidate_cpf')}
                </span>
                <input
                  value={participantCpf}
                  onChange={(event) =>
                    onParticipantCpfChange(formatCpf(event.target.value))
                  }
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={14}
                  autoFocus
                  className="h-11 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-ring"
                  placeholder={t(
                    'onboarding_interviews.meeting.candidate_cpf_placeholder',
                  )}
                />
              </label>
              <label className="grid gap-2">
                <span className="text-sm font-medium text-card-foreground">
                  {t('onboarding_interviews.meeting.candidate_birth_date')}
                </span>
                <input
                  value={participantBirthDate}
                  onChange={(event) =>
                    onParticipantBirthDateChange(
                      formatBirthDate(event.target.value),
                    )
                  }
                  type="text"
                  inputMode="numeric"
                  autoComplete="bday"
                  maxLength={10}
                  placeholder="dd/mm/aaaa"
                  className="h-11 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-ring"
                />
              </label>
            </div>
          ) : null}

          {mediaError ? (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-100">
              {t(mediaError)}
            </div>
          ) : null}

          <Button
            type="button"
            className="h-11 bg-sky-500 text-white hover:bg-sky-600"
            disabled={isPreparingConnection}
            onClick={() =>
              onJoin({
                audioEnabled,
                videoEnabled,
                audioInputDeviceId: audioInputDeviceId || undefined,
                videoInputDeviceId: videoInputDeviceId || undefined,
                audioOutputDeviceId: audioOutputDeviceId || undefined,
                microphoneSensitivity,
              })
            }
          >
            {isPreparingConnection ? (
              <span className="inline-flex items-center gap-2">
                <LoaderCircle className="h-4 w-4 animate-spin" />
                {t('onboarding_interviews.meeting.connecting')}
              </span>
            ) : (
              notice?.actionLabel ||
              t('onboarding_interviews.meeting.join_button')
            )}
          </Button>
        </aside>
      </main>
    </div>
  );
};

const MeetingState = ({
  title,
  description,
  onClose,
  onRetry,
}: {
  title: string;
  description: string;
  onClose: () => void;
  onRetry?: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="relative flex h-dvh items-center justify-center bg-background p-6">
      <div className="absolute right-4 top-4">
        <SystemPreferencesMenu />
      </div>
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-card-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
        <div className="mt-5 flex justify-center gap-2">
          {onRetry ? (
            <Button onClick={onRetry}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('onboarding_interviews.meeting.retry')}
            </Button>
          ) : null}
          <Button variant="outline" onClick={onClose}>
            <X className="mr-2 h-4 w-4" />
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
};
